import type React from "react";
import { AbsoluteFill } from "remotion";

// Exports fake shaped blurs (radial, directional, lens) by averaging shifted copies.
// Layer i at opacity 1/(i+1) over the previous layers weights every copy equally.
export const StackedBlur: React.FC<{
  transforms: (string | undefined)[];
  children: React.ReactNode;
}> = ({ transforms, children }) =>
  transforms.map((transform, i) => (
    <AbsoluteFill key={i} style={{ opacity: 1 / (i + 1), transform }}>
      {children}
    </AbsoluteFill>
  ));

const steps = (copies: number) =>
  Array.from({ length: copies }, (_, i) => i / (copies - 1));

export function radialTransforms(spread: number, copies: number) {
  return steps(copies).map((t) =>
    t === 0 ? undefined : `scale(${1 + spread * t})`,
  );
}

// Copies trail behind the motion, `distance` in percent of the frame.
export function directionalTransforms(
  axis: "x" | "y",
  distance: number,
  copies: number,
) {
  return steps(copies).map((t) =>
    t === 0 ? undefined : `translate${axis.toUpperCase()}(${distance * t}%)`,
  );
}

// A centre copy plus a ring approximates a lens (disk) blur.
export function lensTransforms(radius: number, ring: number) {
  return [
    undefined,
    ...Array.from({ length: ring }, (_, i) => {
      const angle = (2 * Math.PI * i) / ring;
      return `translate(${radius * Math.cos(angle)}px, ${radius * Math.sin(angle)}px)`;
    }),
  ];
}
